import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent } from 'react';
import { useAuth } from '../../systems/auth-context';
import { formatShields, rankPlayers, type LeaderboardResponse } from '../../systems/leaderboard';
import { fetchLeaderboard, fetchPseudoSuggestion, savePlayerProfile } from '../../systems/leaderboard-api';
import {
  cleanPseudo,
  DEFAULT_AVATAR_COLOR,
  isPseudoSubmittable,
  PROFILE_COLORS,
  PSEUDO_MAX_LENGTH,
} from '../../systems/player-profile';
import { SHARED_ASSETS } from '../../shared-assets';
import { PlayerAvatar } from './player-avatar';
import { LeaderboardRow } from './leaderboard-row';
import styles from './profile-setup-screen.module.css';

interface ProfileSetupScreenProps {
  /** `first` : à la première connexion, avant d'entrer dans le jeu. `edit` : depuis le profil. */
  mode: 'first' | 'edit';
  onDone: () => void;
  onCancel?: () => void;
}

/** Écran profil : le pseudo et la couleur qui forment l'avatar du joueur dans le classement. */
export function ProfileSetupScreen({ mode, onDone, onCancel }: ProfileSetupScreenProps) {
  const { user, updateSession } = useAuth();
  const [pseudo, setPseudo] = useState(mode === 'edit' ? (user?.pseudo ?? '') : '');
  const [color, setColor] = useState(user?.avatar_color || DEFAULT_AVATAR_COLOR);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [board, setBoard] = useState<LeaderboardResponse | null>(null);
  const touchedRef = useRef(mode === 'edit' && Boolean(user?.pseudo));
  const colorRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!touchedRef.current) {
      fetchPseudoSuggestion()
        .then((suggestion) => {
          if (cancelled || touchedRef.current) return;
          setPseudo(suggestion.pseudo);
          setColor(suggestion.color);
        })
        .catch(() => {
          // Pas de suggestion : le joueur tape son pseudo.
        });
    }
    fetchLeaderboard()
      .then((data) => {
        if (!cancelled) setBoard(data);
      })
      .catch(() => {
        // L'aperçu de la ligne reste sans rang.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const displayName = cleanPseudo(pseudo);
  const canSubmit = isPseudoSubmittable(pseudo) && !isSaving;

  /** La ligne telle qu'elle paraîtra dans « Le plus complet ». */
  const preview = useMemo(() => {
    const entry = board?.me.entry;
    if (!board || !entry) return { rank: null, value: '' };
    const mine = { ...entry, pseudo: displayName, color, isMe: true };
    const ranked = rankPlayers([...board.players.filter((player) => !player.isMe), mine], 'complet');
    return { rank: ranked.indexOf(mine) + 1, value: formatShields(mine, board.shieldsMax) };
  }, [board, displayName, color]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setError('');
    setIsSaving(true);
    try {
      const updated = await savePlayerProfile<Parameters<typeof updateSession>[0]>(displayName, color);
      updateSession(updated);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue.');
      setIsSaving(false);
    }
  };

  const selectColorAt = (index: number) => {
    const nextIndex = (index + PROFILE_COLORS.length) % PROFILE_COLORS.length;
    const next = PROFILE_COLORS[nextIndex];
    if (!next) return;
    setColor(next.value);
    colorRefs.current[nextIndex]?.focus();
  };

  const handleColorKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      selectColorAt(index + 1);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      selectColorAt(index - 1);
    }
  };

  const selectedIndex = Math.max(0, PROFILE_COLORS.findIndex((entry) => entry.value === color));

  return (
    <div
      className={`${styles.screen} ${mode === 'edit' ? styles.overlay : ''}`}
      role={mode === 'edit' ? 'dialog' : undefined}
      aria-modal={mode === 'edit' ? true : undefined}
      aria-labelledby="profile-setup-title"
      onKeyDown={(event) => {
        if (mode === 'edit' && event.key === 'Escape' && onCancel) onCancel();
      }}
    >
      <div className={styles.background} style={{ backgroundImage: `url(${SHARED_ASSETS.profileBackground})` }} aria-hidden="true" />
      <div className={styles.veil} aria-hidden="true" />

      <form className={styles.layout} onSubmit={handleSubmit} noValidate>
        <div className={styles.fields}>
          <div>
            <p className="etiq-sable">{mode === 'edit' ? 'Votre profil' : 'Avant de commencer'}</p>
            <h1 id="profile-setup-title" className={styles.title}>
              Comment voulez-vous apparaître&nbsp;?
            </h1>
            <p className={styles.lead}>Votre pseudo et votre avatar vous représentent dans le classement.</p>
          </div>

          <div className={styles.field}>
            <label className="etiq" htmlFor="profile-pseudo">
              Pseudo
            </label>
            <input
              id="profile-pseudo"
              className={styles.input}
              value={pseudo}
              maxLength={PSEUDO_MAX_LENGTH}
              autoComplete="off"
              spellCheck={false}
              aria-describedby="profile-pseudo-hint"
              aria-invalid={error ? true : undefined}
              onChange={(event) => {
                touchedRef.current = true;
                setPseudo(event.target.value);
                setError('');
              }}
            />
            <p id="profile-pseudo-hint" className={error ? styles.error : styles.hint} aria-live="polite">
              {error || `De 2 à ${PSEUDO_MAX_LENGTH} caractères. Évitez votre vrai nom.`}
            </p>
          </div>

          <div className={styles.field}>
            <span className="etiq" id="profile-color-label">
              Couleur
            </span>
            <div className={styles.colors} role="radiogroup" aria-labelledby="profile-color-label">
              {PROFILE_COLORS.map((entry, index) => {
                const checked = index === selectedIndex;
                return (
                  <button
                    key={entry.value}
                    ref={(node) => {
                      colorRefs.current[index] = node;
                    }}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    aria-label={entry.label}
                    tabIndex={checked ? 0 : -1}
                    className={styles.color}
                    style={{ '--c': entry.value } as CSSProperties}
                    onClick={() => setColor(entry.value)}
                    onKeyDown={(event) => handleColorKeyDown(event, index)}
                  />
                );
              })}
            </div>
          </div>
        </div>

        <aside className={`${styles.preview} verre`} aria-label="Aperçu">
          <p className="etiq">Ce que voient les autres joueurs</p>
          <PlayerAvatar pseudo={displayName} color={color} size={112} className={styles.previewAvatar} />
          <p className={styles.previewName}>{displayName || '…'}</p>
          <div className={styles.previewLine}>
            <p className="etiq">Dans la liste</p>
            <ul className={styles.previewList}>
              <LeaderboardRow rank={preview.rank} pseudo={displayName || '…'} color={color} value={preview.value} />
            </ul>
          </div>
          <button type="submit" className={styles.submit} disabled={!canSubmit}>
            {isSaving ? 'Enregistrement…' : mode === 'edit' ? 'Enregistrer' : 'Entrer dans le jeu'}
          </button>
          {mode === 'edit' && onCancel && (
            <button type="button" className={`btn-ghost ${styles.cancel}`} onClick={onCancel}>
              Annuler
            </button>
          )}
        </aside>
      </form>
    </div>
  );
}
